'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import Toast from '@/components/ui/Toast';
import {
  getProvinsi,
  getKabupatenByProvinsi,
  getKecamatanByKabupaten,
  getKelurahanByKecamatan,
  Provinsi,
  KabupatenKota,
  Kecamatan,
  Kelurahan,
} from '@/lib/utils/indonesiaWilayah';

interface AgamaOption { id: string; nama: string; }
interface KompetensiOption { id: string; nama: string; }
interface PekerjaanOption { id: string; nama: string; }

interface WhitelistData {
  id: string;
  npsn_sekolah_asal: string;
  nama_sekolah_asal: string | null;
  nisn: string;
  nama: string;
  jenis_kelamin: string | null;
  sistem_penerimaan: string | null;
  tahap_seleksi: string | null;
  kompetensi_keahlian: string | null;
}

type TabKey = 'identitas' | 'pribadi' | 'alamat' | 'ortu';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'identitas', label: 'Identitas Utama' },
  { key: 'pribadi', label: 'Data Pribadi' },
  { key: 'alamat', label: 'Alamat' },
  { key: 'ortu', label: 'Orang Tua/Wali' },
];

const PENDIDIKAN_OPTIONS = ['', 'Tidak Sekolah', 'SD', 'SMP', 'SMA/SMK', 'D1', 'D2', 'D3', 'D4/S1', 'S2', 'S3'];
const PENGHASILAN_OPTIONS = [
  '', 'Tidak Berpenghasilan', 'Kurang dari Rp 500.000', 'Rp 500.000 - Rp 999.999',
  'Rp 1.000.000 - Rp 1.999.999', 'Rp 2.000.000 - Rp 4.999.999',
  'Rp 5.000.000 - Rp 20.000.000', 'Lebih dari Rp 20.000.000',
];

const inputCls = 'w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all';
const inputLockedCls = 'w-full px-3.5 py-2.5 rounded-xl border border-green-300 bg-green-50 text-sm text-green-800 font-medium cursor-not-allowed';

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function DaftarUlangPage() {
  const router = useRouter();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Step 1: NPSN + NISN + Kode
  const [tahunMasuk] = useState(2026);
  const [jalurPenerimaan] = useState('SPMB 2026');

  // NPSN state
  const [npsn, setNpsn] = useState('');
  const [namaSekolah, setNamaSekolah] = useState('');
  const [npsnLoading, setNpsnLoading] = useState(false);
  const [npsnError, setNpsnError] = useState('');
  const npsnDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // NISN state
  const [nisn, setNisn] = useState('');

  // Kode state
  const [kodeInput, setKodeInput] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [kodeVerified, setKodeVerified] = useState(false);
  const [kodeId, setKodeId] = useState('');

  // Whitelist state
  const [whitelistData, setWhitelistData] = useState<WhitelistData | null>(null);
  const [whitelistChecked, setWhitelistChecked] = useState(false);

  // Step 2: Form state
  const [activeTab, setActiveTab] = useState<TabKey>('identitas');
  const [agamaList, setAgamaList] = useState<AgamaOption[]>([]);
  const [kompetensiList, setKompetensiList] = useState<KompetensiOption[]>([]);
  const [pekerjaanList, setPekerjaanList] = useState<PekerjaanOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Wilayah
  const [provinsiList, setProvinsiList] = useState<Provinsi[]>([]);
  const [kabupatenList, setKabupatenList] = useState<KabupatenKota[]>([]);
  const [kecamatanList, setKecamatanList] = useState<Kecamatan[]>([]);
  const [kelurahanList, setKelurahanList] = useState<Kelurahan[]>([]);
  const [selectedProvinsiId, setSelectedProvinsiId] = useState('');
  const [selectedKabupatenId, setSelectedKabupatenId] = useState('');
  const [selectedKecamatanId, setSelectedKecamatanId] = useState('');

  const [form, setForm] = useState({
    nisn: '', nama: '', tempat_lahir: '', tgl_lahir: '', jenis_kelamin: '',
    agama_id: '', email: '', no_hp: '', kompetensi_keahlian_id: '', asal_sekolah: '',
    nik: '', alamat: '', rt: '', rw: '', dusun: '', kelurahan: '', kecamatan: '', kode_pos: '',
    provinsi: '', kota_kab: '', jenis_tinggal: '', alat_transportasi: '', telepon: '',
    no_kk: '', anak_ke: '', jml_saudara_kandung: '', berat_badan: '', tinggi_badan: '',
    ayah_nama: '', ayah_tahun_lahir: '', ayah_pendidikan: '', ayah_pekerjaan: '', ayah_penghasilan: '', ayah_nik: '',
    ibu_nama: '', ibu_tahun_lahir: '', ibu_pendidikan: '', ibu_pekerjaan: '', ibu_penghasilan: '', ibu_nik: '',
    wali_nama: '', wali_tahun_lahir: '', wali_pendidikan: '', wali_pekerjaan: '', wali_penghasilan: '', wali_nik: '',
  });

  // Locked fields from whitelist (cannot be changed by student)
  const isFromWhitelist = whitelistData !== null && kodeVerified;

  useEffect(() => {
    const supabase = createClient();
    supabase.from('agama_master').select('id, nama').order('nama').then(({ data }) => {
      if (data) setAgamaList(data);
    });
    supabase.from('kompetensi_keahlian').select('id, nama').order('nama').then(({ data }) => {
      if (data) setKompetensiList(data);
    });
    supabase.from('pekerjaan_master').select('id, nama').order('nama').then(({ data }) => {
      if (data) setPekerjaanList(data);
    });
    getProvinsi().then((data) => setProvinsiList(data));
  }, []);

  // Debounced NPSN lookup
  useEffect(() => {
    if (!npsn.trim() || npsn.trim().length < 8) {
      setNamaSekolah('');
      setNpsnError('');
      return;
    }
    if (npsnDebounceRef.current) clearTimeout(npsnDebounceRef.current);
    npsnDebounceRef.current = setTimeout(async () => {
      setNpsnLoading(true);
      setNpsnError('');
      setNamaSekolah('');
      try {
        const res = await fetch(`/api/sekolah?npsn=${encodeURIComponent(npsn.trim())}`);
        const json = await res.json();
        // Handle various possible response shapes from sekolah.devapi.id
        const sekolah =
          json?.data?.sekolah ??
          json?.data?.satuanPendidikan ??
          json?.sekolah ??
          (Array.isArray(json?.data) ? json.data[0] : null) ??
          (Array.isArray(json) ? json[0] : null) ??
          json?.data ??
          null;
        const namaResult =
          sekolah?.nama_sekolah ??
          sekolah?.nama ??
          sekolah?.sekolah ??
          sekolah?.school_name ??
          null;
        if (namaResult) {
          setNamaSekolah(namaResult);
        } else {
          setNpsnError('Sekolah tidak ditemukan. Periksa kembali NPSN.');
        }
      } catch {
        setNpsnError('Gagal menghubungi API sekolah. Coba lagi.');
      } finally {
        setNpsnLoading(false);
      }
    }, 700);
    return () => {
      if (npsnDebounceRef.current) clearTimeout(npsnDebounceRef.current);
    };
  }, [npsn]);

  const handleProvinsiChange = async (id: string) => {
    setSelectedProvinsiId(id);
    setSelectedKabupatenId('');
    setSelectedKecamatanId('');
    const data = await getKabupatenByProvinsi(id);
    setKabupatenList(data);
    setKecamatanList([]);
    setKelurahanList([]);
    const provList = await getProvinsi();
    const prov = provList.find((p) => p.id === id);
    setForm((f) => ({ ...f, provinsi: prov?.nama || '', kota_kab: '', kecamatan: '', kelurahan: '' }));
  };

  const handleKabupatenChange = async (id: string) => {
    setSelectedKabupatenId(id);
    setSelectedKecamatanId('');
    const data = await getKecamatanByKabupaten(id);
    setKecamatanList(data);
    setKelurahanList([]);
    const kab = kabupatenList.find((k) => k.id === id);
    setForm((f) => ({ ...f, kota_kab: kab?.nama || '', kecamatan: '', kelurahan: '' }));
  };

  const handleKecamatanChange = async (id: string) => {
    setSelectedKecamatanId(id);
    const data = await getKelurahanByKecamatan(id);
    setKelurahanList(data);
    const kec = kecamatanList.find((k) => k.id === id);
    setForm((f) => ({ ...f, kecamatan: kec?.nama || '', kelurahan: '' }));
  };

  const handleVerifikasi = async () => {
    if (!npsn.trim()) { setVerifyError('Masukkan NPSN sekolah asal terlebih dahulu.'); return; }
    if (!namaSekolah) { setVerifyError('NPSN sekolah belum terverifikasi dari API.'); return; }
    if (!nisn.trim()) { setVerifyError('Masukkan NISN terlebih dahulu.'); return; }
    if (!kodeInput.trim()) { setVerifyError('Masukkan kode daftar ulang.'); return; }
    setVerifyError('');
    setIsVerifying(true);
    try {
      // 1. Check whitelist
      const wlRes = await fetch(`/api/admin/siswa-baru/whitelist?npsn=${encodeURIComponent(npsn.trim())}&nisn=${encodeURIComponent(nisn.trim())}`);
      const wlJson = await wlRes.json();

      if (!wlJson.found) {
        setVerifyError('NPSN dan NISN tidak ditemukan dalam daftar siswa baru yang telah disetujui admin.');
        return;
      }

      const wl: WhitelistData = wlJson.data;

      // 2. Check kode daftar ulang
      const supabase = createClient();
      const { data: kodeData, error: kodeError } = await supabase
        .from('kode_daftar_ulang')
        .select('id, kode, is_active')
        .eq('kode', kodeInput.trim().toUpperCase())
        .eq('is_active', true)
        .maybeSingle();

      if (kodeError) throw kodeError;
      if (!kodeData) {
        setVerifyError('Kode daftar ulang tidak valid atau sudah tidak aktif.');
        return;
      }

      // 3. All valid — fill form from whitelist
      setWhitelistData(wl);
      setWhitelistChecked(true);
      setKodeId(kodeData.id);
      setKodeVerified(true);

      // Resolve kompetensi_keahlian_id from whitelist name
      let resolvedKompetensiId = '';
      if (wl.kompetensi_keahlian) {
        const supabaseClient = createClient();
        const trimmedName = wl.kompetensi_keahlian.trim();
        // Try exact match first (case-insensitive)
        const { data: kkExact } = await supabaseClient
          .from('kompetensi_keahlian')
          .select('id, nama')
          .ilike('nama', trimmedName)
          .maybeSingle();
        if (kkExact) {
          resolvedKompetensiId = kkExact.id;
        } else {
          // Try partial match (contains)
          const { data: kkList } = await supabaseClient
            .from('kompetensi_keahlian')
            .select('id, nama')
            .ilike('nama', `%${trimmedName}%`)
            .limit(1);
          if (kkList && kkList.length > 0) {
            resolvedKompetensiId = kkList[0].id;
          }
        }
      }

      // Auto-fill locked fields from whitelist
      setForm((f) => ({
        ...f,
        nisn: wl.nisn,
        nama: wl.nama,
        jenis_kelamin: wl.jenis_kelamin || '',
        asal_sekolah: wl.nama_sekolah_asal || namaSekolah,
        kompetensi_keahlian_id: resolvedKompetensiId,
      }));

    } catch {
      setVerifyError('Terjadi kesalahan saat verifikasi. Coba lagi.');
    } finally {
      setIsVerifying(false);
    }
  };

  const setField = (key: string, value: string) => {
    // Prevent changing locked whitelist fields
    if (isFromWhitelist && ['nama', 'jenis_kelamin', 'nisn', 'asal_sekolah', 'kompetensi_keahlian_id'].includes(key)) return;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.nama.trim()) { setToast({ message: 'Nama wajib diisi.', type: 'error' }); return; }
    setIsSaving(true);
    try {
      const supabase = createClient();
      const sistemPenerimaan = whitelistData?.sistem_penerimaan || '';
      const tahapSeleksi = whitelistData?.tahap_seleksi || null;

      const payload: Record<string, unknown> = {
        kode_daftar_ulang_id: kodeId || null,
        kode_digunakan: kodeInput.trim().toUpperCase(),
        tahun_masuk: tahunMasuk,
        jalur_penerimaan: jalurPenerimaan,
        sistem_penerimaan: sistemPenerimaan,
        tahap_seleksi: tahapSeleksi,
        status: 'menunggu',
        ...Object.fromEntries(
          Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
        ),
        anak_ke: form.anak_ke ? parseInt(form.anak_ke) : null,
        jml_saudara_kandung: form.jml_saudara_kandung ? parseInt(form.jml_saudara_kandung) : null,
        berat_badan: form.berat_badan ? parseFloat(form.berat_badan) : null,
        tinggi_badan: form.tinggi_badan ? parseFloat(form.tinggi_badan) : null,
        ayah_tahun_lahir: form.ayah_tahun_lahir ? parseInt(form.ayah_tahun_lahir) : null,
        ibu_tahun_lahir: form.ibu_tahun_lahir ? parseInt(form.ibu_tahun_lahir) : null,
        wali_tahun_lahir: form.wali_tahun_lahir ? parseInt(form.wali_tahun_lahir) : null,
      };

      const { data: insertedData, error } = await supabase.from('pendaftaran_ulang').insert(payload).select('id').single();
      if (error) throw error;

      // Mark whitelist entry as used
      if (whitelistData?.id) {
        await supabase.from('siswa_baru_whitelist').update({ is_used: true }).eq('id', whitelistData.id);
      }

      // Save log
      try {
        let ipAddress = '';
        let browserInfo = '';
        try {
          const ipRes = await fetch('https://api.ipify.org?format=json');
          const ipJson = await ipRes.json();
          ipAddress = ipJson.ip || '';
        } catch { /* ignore */ }
        if (typeof navigator !== 'undefined') {
          browserInfo = navigator.userAgent;
        }
        await supabase.from('daftar_ulang_log').insert({
          pendaftaran_ulang_id: insertedData?.id || null,
          nama_siswa: form.nama,
          nisn: form.nisn || null,
          kode_digunakan: kodeInput.trim().toUpperCase(),
          sistem_penerimaan: sistemPenerimaan,
          tahap_seleksi: tahapSeleksi,
          ip_address: ipAddress || null,
          user_agent: browserInfo || null,
          browser: browserInfo ? browserInfo.substring(0, 200) : null,
          tanggal_daftar: new Date().toISOString(),
        });
      } catch { /* log error non-fatal */ }

      setSavedAt(new Date());
      setSavedSuccess(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Terjadi kesalahan.';
      setToast({ message: msg, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (savedSuccess) {
    const pad = (n: number) => String(n).padStart(2, '0');
    const tgl = savedAt
      ? `${pad(savedAt.getDate())} - ${pad(savedAt.getMonth() + 1)} - ${savedAt.getFullYear()} - ${pad(savedAt.getHours())} : ${pad(savedAt.getMinutes())} : ${pad(savedAt.getSeconds())}`
      : '';

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Terima Kasih, Proses Daftar Ulang Berhasil</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-left space-y-1">
            <p className="text-sm text-gray-700"><span className="font-semibold">NISN</span> : {form.nisn || '-'}</p>
            <p className="text-sm text-gray-700"><span className="font-semibold">Nama Siswa</span> : {form.nama || '-'}</p>
            <p className="text-sm text-gray-700"><span className="font-semibold">Jalur</span> : {jalurPenerimaan} — {whitelistData?.sistem_penerimaan || '-'}</p>
            {whitelistData?.tahap_seleksi && (
              <p className="text-sm text-gray-700"><span className="font-semibold">Tahap</span> : {whitelistData.tahap_seleksi}</p>
            )}
          </div>
          {tgl && (
            <p className="text-sm text-gray-500 mb-4">{tgl}</p>
          )}
          <p className="text-gray-600 mb-2">Data pendaftaran ulang kamu telah diterima dan sedang dalam antrian verifikasi admin.</p>
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6">
            ⏳ Akun kamu akan diaktifkan oleh admin setelah data diverifikasi. Kamu akan bisa login setelah akun diaktifkan.
          </p>
          <Link href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors">
            Kembali ke Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
            <AppLogo size={22} />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">SMK Negeri 5 Malang</p>
            <h1 className="text-base font-bold text-gray-900 leading-tight">Daftar Ulang Siswa Baru 2026</h1>
          </div>
          <div className="ml-auto">
            <Link href="/" className="text-sm text-blue-600 hover:underline font-medium">← Kembali Login</Link>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Step 1: Verifikasi NPSN + NISN + Kode */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
            <h2 className="text-lg font-bold text-gray-900">Verifikasi Data Siswa</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* Tahun Masuk */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tahun Masuk</label>
              <input type="text" value={tahunMasuk} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm font-medium text-gray-600 cursor-not-allowed" />
            </div>
            {/* Jalur Penerimaan */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Jalur Penerimaan</label>
              <input type="text" value={jalurPenerimaan} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm font-medium text-gray-600 cursor-not-allowed" />
            </div>
          </div>

          {/* NPSN Sekolah Asal */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              NPSN Sekolah Asal <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">Masukkan NPSN SMP/MTs asal kamu. Nama sekolah akan otomatis terisi.</p>
            <div className="flex gap-3">
              <input
                type="text"
                value={npsn}
                onChange={(e) => { setNpsn(e.target.value.trim()); setVerifyError(''); setKodeVerified(false); setWhitelistData(null); }}
                disabled={kodeVerified}
                placeholder="Contoh: 20539734"
                className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed ${
                  namaSekolah ? 'border-green-400 bg-green-50' : npsnError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                }`}
              />
              {npsnLoading && (
                <div className="flex items-center px-3">
                  <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
            </div>
            {namaSekolah && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-green-800 font-medium">{namaSekolah}</span>
              </div>
            )}
            {npsnError && <p className="mt-1.5 text-xs text-red-600">{npsnError}</p>}
          </div>

          {/* NISN */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              NISN <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nisn}
              onChange={(e) => { setNisn(e.target.value.trim()); setVerifyError(''); setKodeVerified(false); setWhitelistData(null); }}
              disabled={kodeVerified}
              placeholder="Nomor Induk Siswa Nasional"
              className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed ${
                kodeVerified ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            />
          </div>

          {/* Kode Daftar Ulang */}
          <div className="border-t border-gray-100 pt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Kode Daftar Ulang <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">Masukkan kode yang telah disiapkan oleh admin sekolah.</p>
            <div className="flex gap-3">
              <input
                type="text"
                value={kodeInput}
                onChange={(e) => { setKodeInput(e.target.value.toUpperCase()); setVerifyError(''); setKodeVerified(false); setWhitelistData(null); }}
                disabled={kodeVerified}
                placeholder="Contoh: SPMB2026-001"
                className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-mono font-medium focus:outline-none focus:ring-2 focus:ring-blue-300 uppercase ${
                  kodeVerified ? 'border-green-400 bg-green-50 text-green-700' : verifyError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                } disabled:cursor-not-allowed`}
              />
              {!kodeVerified ? (
                <button
                  onClick={handleVerifikasi}
                  disabled={isVerifying || !kodeInput.trim() || !npsn.trim() || !nisn.trim()}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {isVerifying ? 'Memverifikasi...' : 'VERIFIKASI'}
                </button>
              ) : (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-green-100 text-green-700 rounded-xl text-sm font-semibold">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Terverifikasi
                </div>
              )}
            </div>
            {verifyError && <p className="mt-1.5 text-xs text-red-600">{verifyError}</p>}
            {kodeVerified && whitelistData && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-xs font-semibold text-green-700 mb-2">✓ Data terverifikasi dari whitelist admin:</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-green-800">
                  <span><span className="font-medium">Nama:</span> {whitelistData.nama}</span>
                  <span><span className="font-medium">JK:</span> {whitelistData.jenis_kelamin === 'L' ? 'Laki-laki' : whitelistData.jenis_kelamin === 'P' ? 'Perempuan' : '-'}</span>
                  <span><span className="font-medium">Sistem:</span> {whitelistData.sistem_penerimaan || '-'}</span>
                  <span><span className="font-medium">Tahap:</span> {whitelistData.tahap_seleksi || '-'}</span>
                  {whitelistData.kompetensi_keahlian && (
                    <span className="col-span-2"><span className="font-medium">Kompetensi Keahlian:</span> {whitelistData.kompetensi_keahlian}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Form Data Siswa */}
        {kodeVerified && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
              <h2 className="text-lg font-bold text-gray-900">Data Diri Siswa</h2>
            </div>
            {isFromWhitelist && (
              <div className="mb-5 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Nama, Jenis Kelamin, Kompetensi Keahlian, dan Asal Sekolah diisi otomatis dari data admin dan <strong>tidak dapat diubah</strong>.</span>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 min-w-max px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeTab === tab.key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab: Identitas Utama */}
            {activeTab === 'identitas' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Nama Lengkap" required>
                  {isFromWhitelist ? (
                    <input type="text" value={form.nama} readOnly className={inputLockedCls} />
                  ) : (
                    <input type="text" value={form.nama} onChange={(e) => setField('nama', e.target.value)} className={inputCls} placeholder="Nama lengkap sesuai akta lahir" />
                  )}
                </FormField>
                <FormField label="NISN">
                  {isFromWhitelist ? (
                    <input type="text" value={form.nisn} readOnly className={inputLockedCls} />
                  ) : (
                    <input type="text" value={form.nisn} onChange={(e) => setField('nisn', e.target.value)} className={inputCls} placeholder="Nomor Induk Siswa Nasional" />
                  )}
                </FormField>
                <FormField label="Jenis Kelamin">
                  {isFromWhitelist ? (
                    <input type="text" value={form.jenis_kelamin === 'L' ? 'Laki-laki' : form.jenis_kelamin === 'P' ? 'Perempuan' : form.jenis_kelamin} readOnly className={inputLockedCls} />
                  ) : (
                    <select value={form.jenis_kelamin} onChange={(e) => setField('jenis_kelamin', e.target.value)} className={inputCls}>
                      <option value="">-- Pilih --</option>
                      <option value="L">Laki-laki</option>
                      <option value="P">Perempuan</option>
                    </select>
                  )}
                </FormField>
                <FormField label="Agama">
                  <select value={form.agama_id} onChange={(e) => setField('agama_id', e.target.value)} className={inputCls}>
                    <option value="">-- Pilih Agama --</option>
                    {agamaList.map((a) => <option key={a.id} value={a.id}>{a.nama}</option>)}
                  </select>
                </FormField>
                <FormField label="Kompetensi Keahlian">
                  {isFromWhitelist ? (
                    <input
                      type="text"
                      value={whitelistData?.kompetensi_keahlian || kompetensiList.find((k) => k.id === form.kompetensi_keahlian_id)?.nama || '-'}
                      readOnly
                      className={inputLockedCls}
                    />
                  ) : (
                    <select value={form.kompetensi_keahlian_id} onChange={(e) => setField('kompetensi_keahlian_id', e.target.value)} className={inputCls}>
                      <option value="">-- Pilih Kompetensi --</option>
                      {kompetensiList.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                    </select>
                  )}
                </FormField>
                <FormField label="Email">
                  <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className={inputCls} placeholder="email@contoh.com" />
                </FormField>
                <FormField label="No. HP">
                  <input type="text" value={form.no_hp} onChange={(e) => setField('no_hp', e.target.value)} className={inputCls} placeholder="08xxxxxxxxxx" />
                </FormField>
                <FormField label="Asal Sekolah">
                  {isFromWhitelist ? (
                    <input type="text" value={form.asal_sekolah} readOnly className={inputLockedCls} />
                  ) : (
                    <input type="text" value={form.asal_sekolah} onChange={(e) => setField('asal_sekolah', e.target.value)} className={inputCls} placeholder="Nama SMP/MTs asal" />
                  )}
                </FormField>
              </div>
            )}

            {/* Tab: Data Pribadi */}
            {activeTab === 'pribadi' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Tempat Lahir">
                  <input type="text" value={form.tempat_lahir} onChange={(e) => setField('tempat_lahir', e.target.value)} className={inputCls} placeholder="Kota tempat lahir" />
                </FormField>
                <FormField label="Tanggal Lahir">
                  <input type="date" value={form.tgl_lahir} onChange={(e) => setField('tgl_lahir', e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="NIK Siswa">
                  <input type="text" value={form.nik} onChange={(e) => setField('nik', e.target.value)} className={inputCls} placeholder="16 digit NIK" maxLength={16} />
                </FormField>
                <FormField label="No. KK">
                  <input type="text" value={form.no_kk} onChange={(e) => setField('no_kk', e.target.value)} className={inputCls} placeholder="Nomor Kartu Keluarga" />
                </FormField>
                <FormField label="Anak Ke">
                  <input type="number" value={form.anak_ke} onChange={(e) => setField('anak_ke', e.target.value)} className={inputCls} placeholder="Urutan anak" min={1} />
                </FormField>
                <FormField label="Jumlah Saudara Kandung">
                  <input type="number" value={form.jml_saudara_kandung} onChange={(e) => setField('jml_saudara_kandung', e.target.value)} className={inputCls} placeholder="0" min={0} />
                </FormField>
                <FormField label="Berat Badan (kg)">
                  <input type="number" value={form.berat_badan} onChange={(e) => setField('berat_badan', e.target.value)} className={inputCls} placeholder="kg" />
                </FormField>
                <FormField label="Tinggi Badan (cm)">
                  <input type="number" value={form.tinggi_badan} onChange={(e) => setField('tinggi_badan', e.target.value)} className={inputCls} placeholder="cm" />
                </FormField>
                <FormField label="Jenis Tinggal">
                  <select value={form.jenis_tinggal} onChange={(e) => setField('jenis_tinggal', e.target.value)} className={inputCls}>
                    <option value="">-- Pilih --</option>
                    {['Bersama Orang Tua', 'Wali', 'Kos', 'Asrama', 'Panti Asuhan', 'Lainnya'].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </FormField>
                <FormField label="Alat Transportasi">
                  <select value={form.alat_transportasi} onChange={(e) => setField('alat_transportasi', e.target.value)} className={inputCls}>
                    <option value="">-- Pilih --</option>
                    {['Jalan Kaki', 'Sepeda', 'Sepeda Motor', 'Mobil Pribadi', 'Angkutan Umum', 'Antar Jemput', 'Lainnya'].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </FormField>
                <FormField label="Telepon Rumah">
                  <input type="text" value={form.telepon} onChange={(e) => setField('telepon', e.target.value)} className={inputCls} placeholder="Nomor telepon rumah" />
                </FormField>
              </div>
            )}

            {/* Tab: Alamat */}
            {activeTab === 'alamat' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <FormField label="Alamat Jalan">
                    <textarea value={form.alamat || ''} onChange={(e) => setField('alamat', e.target.value)} className={`${inputCls} resize-none`} rows={2} placeholder="Nama jalan, nomor rumah" />
                  </FormField>
                </div>
                <FormField label="RT">
                  <input type="text" value={form.rt} onChange={(e) => setField('rt', e.target.value)} className={inputCls} placeholder="001" />
                </FormField>
                <FormField label="RW">
                  <input type="text" value={form.rw} onChange={(e) => setField('rw', e.target.value)} className={inputCls} placeholder="001" />
                </FormField>
                <FormField label="Dusun">
                  <input type="text" value={form.dusun} onChange={(e) => setField('dusun', e.target.value)} className={inputCls} placeholder="Nama dusun" />
                </FormField>
                <FormField label="Kode Pos">
                  <input type="text" value={form.kode_pos} onChange={(e) => setField('kode_pos', e.target.value)} className={inputCls} placeholder="65xxx" maxLength={5} />
                </FormField>
                <FormField label="Provinsi">
                  <select value={selectedProvinsiId} onChange={(e) => handleProvinsiChange(e.target.value)} className={inputCls}>
                    <option value="">-- Pilih Provinsi --</option>
                    {provinsiList.map((p) => <option key={p.id} value={p.id}>{p.nama}</option>)}
                  </select>
                </FormField>
                <FormField label="Kabupaten/Kota">
                  <select value={selectedKabupatenId} onChange={(e) => handleKabupatenChange(e.target.value)} disabled={!selectedProvinsiId} className={inputCls}>
                    <option value="">-- Pilih Kab/Kota --</option>
                    {kabupatenList.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                  </select>
                </FormField>
                <FormField label="Kecamatan">
                  <select value={selectedKecamatanId} onChange={(e) => handleKecamatanChange(e.target.value)} disabled={!selectedKabupatenId} className={inputCls}>
                    <option value="">-- Pilih Kecamatan --</option>
                    {kecamatanList.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                  </select>
                </FormField>
                <FormField label="Kelurahan/Desa">
                  <select value={form.kelurahan} onChange={(e) => setField('kelurahan', e.target.value)} disabled={!selectedKecamatanId} className={inputCls}>
                    <option value="">-- Pilih Kelurahan --</option>
                    {kelurahanList.map((k) => <option key={k.id} value={k.nama}>{k.nama}</option>)}
                  </select>
                </FormField>
              </div>
            )}

            {/* Tab: Orang Tua/Wali */}
            {activeTab === 'ortu' && (
              <div className="space-y-6">
                {/* Ayah */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">A</span>
                    Data Ayah
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label="Nama Ayah"><input type="text" value={form.ayah_nama} onChange={(e) => setField('ayah_nama', e.target.value)} className={inputCls} /></FormField>
                    <FormField label="Tahun Lahir"><input type="number" value={form.ayah_tahun_lahir} onChange={(e) => setField('ayah_tahun_lahir', e.target.value)} className={inputCls} placeholder="1980" /></FormField>
                    <FormField label="Pendidikan"><select value={form.ayah_pendidikan} onChange={(e) => setField('ayah_pendidikan', e.target.value)} className={inputCls}>{PENDIDIKAN_OPTIONS.map((v) => <option key={v} value={v}>{v || '-- Pilih --'}</option>)}</select></FormField>
                    <FormField label="Pekerjaan"><select value={form.ayah_pekerjaan} onChange={(e) => setField('ayah_pekerjaan', e.target.value)} className={inputCls}><option value="">-- Pilih Pekerjaan --</option>{pekerjaanList.map((p) => <option key={p.id} value={p.nama}>{p.nama}</option>)}</select></FormField>
                    <FormField label="Penghasilan"><select value={form.ayah_penghasilan} onChange={(e) => setField('ayah_penghasilan', e.target.value)} className={inputCls}>{PENGHASILAN_OPTIONS.map((v) => <option key={v} value={v}>{v || '-- Pilih --'}</option>)}</select></FormField>
                    <FormField label="NIK Ayah"><input type="text" value={form.ayah_nik} onChange={(e) => setField('ayah_nik', e.target.value)} className={inputCls} maxLength={16} /></FormField>
                  </div>
                </div>
                {/* Ibu */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-pink-100 text-pink-700 rounded-full flex items-center justify-center text-xs font-bold">I</span>
                    Data Ibu
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label="Nama Ibu"><input type="text" value={form.ibu_nama} onChange={(e) => setField('ibu_nama', e.target.value)} className={inputCls} /></FormField>
                    <FormField label="Tahun Lahir"><input type="number" value={form.ibu_tahun_lahir} onChange={(e) => setField('ibu_tahun_lahir', e.target.value)} className={inputCls} placeholder="1982" /></FormField>
                    <FormField label="Pendidikan"><select value={form.ibu_pendidikan} onChange={(e) => setField('ibu_pendidikan', e.target.value)} className={inputCls}>{PENDIDIKAN_OPTIONS.map((v) => <option key={v} value={v}>{v || '-- Pilih --'}</option>)}</select></FormField>
                    <FormField label="Pekerjaan"><select value={form.ibu_pekerjaan} onChange={(e) => setField('ibu_pekerjaan', e.target.value)} className={inputCls}><option value="">-- Pilih Pekerjaan --</option>{pekerjaanList.map((p) => <option key={p.id} value={p.nama}>{p.nama}</option>)}</select></FormField>
                    <FormField label="Penghasilan"><select value={form.ibu_penghasilan} onChange={(e) => setField('ibu_penghasilan', e.target.value)} className={inputCls}>{PENGHASILAN_OPTIONS.map((v) => <option key={v} value={v}>{v || '-- Pilih --'}</option>)}</select></FormField>
                    <FormField label="NIK Ibu"><input type="text" value={form.ibu_nik} onChange={(e) => setField('ibu_nik', e.target.value)} className={inputCls} maxLength={16} /></FormField>
                  </div>
                </div>
                {/* Wali */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">W</span>
                    Data Wali (jika ada)
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label="Nama Wali"><input type="text" value={form.wali_nama} onChange={(e) => setField('wali_nama', e.target.value)} className={inputCls} /></FormField>
                    <FormField label="Tahun Lahir"><input type="number" value={form.wali_tahun_lahir} onChange={(e) => setField('wali_tahun_lahir', e.target.value)} className={inputCls} placeholder="1975" /></FormField>
                    <FormField label="Pendidikan"><select value={form.wali_pendidikan} onChange={(e) => setField('wali_pendidikan', e.target.value)} className={inputCls}>{PENDIDIKAN_OPTIONS.map((v) => <option key={v} value={v}>{v || '-- Pilih --'}</option>)}</select></FormField>
                    <FormField label="Pekerjaan"><select value={form.wali_pekerjaan} onChange={(e) => setField('wali_pekerjaan', e.target.value)} className={inputCls}><option value="">-- Pilih Pekerjaan --</option>{pekerjaanList.map((p) => <option key={p.id} value={p.nama}>{p.nama}</option>)}</select></FormField>
                    <FormField label="Penghasilan"><select value={form.wali_penghasilan} onChange={(e) => setField('wali_penghasilan', e.target.value)} className={inputCls}>{PENGHASILAN_OPTIONS.map((v) => <option key={v} value={v}>{v || '-- Pilih --'}</option>)}</select></FormField>
                    <FormField label="NIK Wali"><input type="text" value={form.wali_nik} onChange={(e) => setField('wali_nik', e.target.value)} className={inputCls} maxLength={16} /></FormField>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation Buttons */}
            <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-100">
              <button
                onClick={() => {
                  const idx = TABS.findIndex((t) => t.key === activeTab);
                  if (idx > 0) setActiveTab(TABS[idx - 1].key);
                }}
                disabled={activeTab === TABS[0].key}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Sebelumnya
              </button>

              {activeTab !== TABS[TABS.length - 1].key ? (
                <button
                  onClick={() => {
                    const idx = TABS.findIndex((t) => t.key === activeTab);
                    setActiveTab(TABS[idx + 1].key);
                  }}
                  className="px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                >
                  Selanjutnya →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="px-6 py-2.5 text-sm font-semibold bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Kirim Pendaftaran
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
